import axios from 'axios';
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SpinnerLoader } from './components/Spinner';
import { TransactionDetail } from './components/TransactionDetail';
import { PaymentFormLayout } from './components/PaymentFormLayout';
import { directSale } from './pixelpay/pixelpay';
import { NotFound } from './components/NotFound';
import { ServiceClosed } from './components/ServiceClosed';
import states from '@pixelpay/sdk-core/lib/assets/states.json'
import phoneCode from '@pixelpay/sdk-core/lib/assets/formats.json'

const baseUrl = process.env.REACT_APP_API_URL
const procotol = process.env.REACT_APP_API_PROTOCOL

// Marca de tarjeta a partir del prefijo (dato NO sensible)
const detectCardBrand = (n) => {
    const s = (n || '').replace(/\s/g, '')
    if (/^4/.test(s)) return 'VISA'
    if (/^(5[1-5]|2[2-7])/.test(s)) return 'MASTERCARD'
    if (/^3[47]/.test(s)) return 'AMEX'
    if (/^6/.test(s)) return 'DISCOVER'
    return ''
}

// Reporta el resultado del intento de pago a la bitácora (best-effort: nunca rompe el flujo).
// Solo envía datos NO sensibles: nunca el número completo ni el CVV.
const reportTransactionLog = async (payload) => {
    try {
        await axios.post(`${procotol}//${baseUrl}/administration/api/register-transaction-log/`, payload)
    } catch (e) {
        // El logueo es secundario; si falla no afecta al usuario.
    }
}

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY

// Carga el script de reCAPTCHA v3 una sola vez.
const loadRecaptchaScript = () => {
    if (!RECAPTCHA_SITE_KEY) return
    if (document.querySelector('script[data-recaptcha]')) return
    const s = document.createElement('script')
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    s.async = true
    s.defer = true
    s.setAttribute('data-recaptcha', '1')
    document.body.appendChild(s)
}

// Obtiene un token de reCAPTCHA v3. Si no está disponible (ej. local sin dominio
// registrado) devuelve '' y el backend decide (en local queda desactivado).
const getRecaptchaToken = async () => {
    try {
        if (RECAPTCHA_SITE_KEY && window.grecaptcha) {
            await new Promise((resolve) => window.grecaptcha.ready(resolve))
            return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'payment' })
        }
    } catch (e) {
        // Si reCAPTCHA falla, no bloqueamos desde el front; el backend valida.
    }
    return ''
}

export const OpenPaymentLink = () => {

    const { slug } = useParams();
    const [stateSelect, setStateSelect] = useState({})
    const [phoneCodeSelected, setPhoneCodeSelected] = useState(null)
    const [isInternational, setIsInternational] = useState(false);
    const [showBillingAddress, setShowBillingAddress] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [validateEntrepreneur, setValidateEntrepreneur] = useState(null);
    const [detailData, setDetailData] = useState(null)
    const [showTransactionDetail, setShowTransactionDetail] = useState(false)
    const [trasactionDetailData, setTrasactionDetailData] = useState(null)
    const [isPosting, setIsPosting] = useState(false);
    const { register, handleSubmit, formState: { errors }, watch } = useForm();
    watch(["country"])
    const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
    const currentYear = new Date().getFullYear()
    const maxYear = currentYear + 20;
    let years = [];

    for (let i = currentYear; i <= maxYear; i++) {
        years.push(i);
    }

    useEffect(() => {
        async function fnValidateEntrepreneur(slug) {
            try {
                setIsLoading(true);
                const url = `${procotol}//${baseUrl}/administration/api/validate-entrepreneur/${slug}/`;
                const res = await axios.get(url);
                setValidateEntrepreneur(res.data)
            } catch (err) {
                const { response } = err
                setValidateEntrepreneur(response.data)
            }
            finally {
                setIsLoading(false)
            }
        }
        fnValidateEntrepreneur(slug)
    }, [slug])

    useEffect(() => {
        loadRecaptchaScript()
    }, [])


    useEffect(() => {
        const subscription = watch((value, { name, type }) => {
            if (name === 'country') {
                if (!!value.country) {
                    setStateSelect(states[value.country])
                    setPhoneCodeSelected(`+${phoneCode[value.country].phone_code}`);
                } else {
                    setStateSelect({})
                    setPhoneCodeSelected(null)
                }
            }
        })

        return () => subscription.unsubscribe()
    }, [watch])

    // Cuando cambie a internacional, activar checkbox de facturación
    useEffect(() => {
        if (isInternational) {
            setShowBillingAddress(true);
        }
    }, [isInternational])


    const onSubmit = async (data) => {
        try {
            setIsPosting(true);
            data = {
                ...data,
                number: data.number.replaceAll(' ', '')
            }
            const id = validateEntrepreneur.id
            const urlStartTransc = `${procotol}//${baseUrl}/administration/api/start-transaction/${slug}/${id}/`;
            const recaptchaToken = await getRecaptchaToken();
            let startTranscData = {
                email: data.email,
                cardholder: data.cardholder,
                amount: data.amount,
                recaptcha_token: recaptchaToken,
            }

            if (!isInternational && !showBillingAddress) {
                data.country = "HN";
                data.state = "HN-FM";
                data.city = "Tegucigalpa";
                data.address = "Tegucigalpa";
                data.phone = "94043476";
                data.zip = "11101";
            }

            const resStartTrans = await axios.post(urlStartTransc, startTranscData);
            const correlative = resStartTrans.data.correlative;
            // Modelo C: el backend firma la orden y devuelve las credenciales; el front
            // cobra con ellas sin conocer el secret.
            const res = await directSale(data, correlative, resStartTrans.data.email, validateEntrepreneur.name, resStartTrans.data.credentials)
            const success = !!(res && res.success);

            reportTransactionLog({
                slug,
                correlative,
                amount: data.amount,
                card_last4: (data.number || '').slice(-4),
                card_brand: detectCardBrand(data.number),
                cardholder: data.cardholder,
                email: data.email,
                result: success ? 'success' : 'error',
                error_code: res?.code ? `${res.code}` : '',
                error_message: success ? '' : (res?.message || 'Fallo en el procesamiento del pago'),
                raw_response: res || null,
            });

            if (success) {
                setTrasactionDetailData(res)
                setDetailData({
                    correlative: correlative,
                    number: data.number.slice(-4),
                    ...startTranscData,
                })
                setShowTransactionDetail(true)
            } else {
                Swal.fire(
                    'Algo no salió bien!',
                    `${res?.message || 'No se pudo procesar el pago.'}`,
                    'error'
                )
            }


        } catch (err) {
            const { response } = err
            Swal.fire(
                'Algo no salió bien!',
                `${response?.data?.msg}`,
                'error'
            )
        }
        finally {
            setIsPosting(false)
        }
    }

    if (isLoading) {
        return <SpinnerLoader />
    }
    if (validateEntrepreneur?.status === 'closed') {
        return <ServiceClosed data={validateEntrepreneur} />
    }
    if (validateEntrepreneur?.status === 'error') {
        return <NotFound />
    }

    // Determinar si mostrar los campos adicionales
    const showAdditionalFields = isInternational || showBillingAddress;

    if (showTransactionDetail) {
        return (
            <TransactionDetail
                data={trasactionDetailData}
                entrepreneur={validateEntrepreneur}
                detailData={detailData}
            />
        )
    }

    return (
        <PaymentFormLayout
            logoUrl={validateEntrepreneur?.logo ? `${procotol}//${baseUrl}/${validateEntrepreneur.logo}` : null}
            title={validateEntrepreneur?.entrepreneurship_name}
            description={validateEntrepreneur?.description}
            register={register}
            errors={errors}
            handleSubmit={handleSubmit}
            onSubmit={onSubmit}
            isInternational={isInternational}
            setIsInternational={setIsInternational}
            showBillingAddress={showBillingAddress}
            setShowBillingAddress={setShowBillingAddress}
            showAdditionalFields={showAdditionalFields}
            stateSelect={stateSelect}
            phoneCodeSelected={phoneCodeSelected}
            months={months}
            years={years}
            isPosting={isPosting}
        />
    )
}
